import React from 'react';
import {
    Settings,
    Server,
    Users,
    Shield,
    Activity,
    Bell,
    Database,
    Globe,
    Key,
    Mail,
    HardDrive,
    Cloud,
    Bot,
    Search,
    ChevronRight,
    Palette,
    PanelTop,
    PanelLeft,
    LayoutDashboard,
    CreditCard,
    PanelRight
} from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AppearanceSettings, UIComponentConfig } from "@/components/CommonFeatures/Appearance";

// Define the UI components specific to this application
const UI_COMPONENTS: UIComponentConfig[] = [
    { id: 'global', label: 'Global', description: 'Apply to all components', icon: Palette },
    { id: 'topNav', label: 'Top Navigation', description: 'Header and main nav bar', icon: PanelTop },
    { id: 'leftPanel', label: 'Left Panel', description: 'Sidebar navigation', icon: PanelLeft },
    { id: 'mainPanel', label: 'Main Panel', description: 'Primary content area', icon: LayoutDashboard },
    { id: 'scoreCards', label: 'Score Cards', description: 'Dashboard metrics & KPIs', icon: CreditCard },
    { id: 'rightPanel', label: 'Right Panel', description: 'Agent and detail panels', icon: PanelRight },
];

export default function SettingsPage() {
    // Settings items for other tabs
    const estateItems = [
        { title: 'Monitored Clients', description: 'Add or remove clients', icon: Users },
        { title: 'Connection Credentials', description: 'Manage access keys', icon: Key },
        { title: 'Groups & Tags', description: 'Organize by region', icon: Server },
        { title: 'Discovery Settings', description: 'Auto-discovery config', icon: Search },
    ];

    const monitoringItems = [
        { title: 'Alert Policies', description: 'Set thresholds', icon: Bell },
        { title: 'Notifications', description: 'Email, Slack, Teams', icon: Mail },
        { title: 'Maintenance', description: 'Schedule downtime', icon: Activity },
        { title: 'Custom Metrics', description: 'SQL query monitors', icon: Database },
    ];

    const agentItems = [
        { title: 'Copilot Behavior', description: 'Tone and autonomy', icon: Settings },
        { title: 'Knowledge Base', description: 'RAG documentation', icon: HardDrive },
        { title: 'API Keys', description: 'OpenAI, Azure, Anthropic', icon: Cloud },
    ];

    const systemItems = [
        { title: 'Users & Permissions', description: 'Team access', icon: Users },
        { title: 'Audit Logs', description: 'Access history', icon: Activity },
        { title: 'Data Retention', description: 'Historical policies', icon: HardDrive },
        { title: 'Updates', description: 'Platform version', icon: Globe },
    ];

    const SettingsGrid = ({ items }: { items: typeof estateItems }) => (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {items.map((item, index) => (
                <Card key={index} className="group hover:shadow-md transition-all cursor-pointer border-gray-200 hover:border-shiftleft-300 bg-white">
                    <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-2">
                            <div className="p-2 bg-gray-50 rounded-md group-hover:bg-shiftleft-50 text-gray-600 group-hover:text-shiftleft-600">
                                <item.icon className="h-4 w-4" />
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-shiftleft-400" />
                        </div>
                        <h3 className="font-medium text-gray-900 text-sm">{item.title}</h3>
                        <p className="text-xs text-gray-500">{item.description}</p>
                    </CardContent>
                </Card>
            ))}
        </div>
    );

    return (
        <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6 animate-in fade-in duration-500">
            {/* Header */}
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Settings</h1>
                <p className="text-gray-500 mt-1">Manage your preferences and platform configuration</p>
            </div>

            {/* Tabbed Layout */}
            <Tabs defaultValue="appearance" className="w-full">
                <TabsList className="w-full justify-start border-b rounded-none h-auto p-0 bg-transparent flex-wrap">
                    <TabsTrigger value="appearance" className="data-[state=active]:border-b-2 data-[state=active]:border-shiftleft-600 data-[state=active]:text-shiftleft-700 rounded-none px-4 py-3 text-sm font-medium">
                        <Palette className="h-4 w-4 mr-2" />
                        Appearance
                    </TabsTrigger>
                    <TabsTrigger value="estate" className="data-[state=active]:border-b-2 data-[state=active]:border-shiftleft-600 data-[state=active]:text-shiftleft-700 rounded-none px-4 py-3 text-sm font-medium">
                        <Globe className="h-4 w-4 mr-2" />
                        Estate
                    </TabsTrigger>
                    <TabsTrigger value="monitoring" className="data-[state=active]:border-b-2 data-[state=active]:border-shiftleft-600 data-[state=active]:text-shiftleft-700 rounded-none px-4 py-3 text-sm font-medium">
                        <Activity className="h-4 w-4 mr-2" />
                        Monitoring
                    </TabsTrigger>
                    <TabsTrigger value="agent" className="data-[state=active]:border-b-2 data-[state=active]:border-shiftleft-600 data-[state=active]:text-shiftleft-700 rounded-none px-4 py-3 text-sm font-medium">
                        <Bot className="h-4 w-4 mr-2" />
                        Agent
                    </TabsTrigger>
                    <TabsTrigger value="system" className="data-[state=active]:border-b-2 data-[state=active]:border-shiftleft-600 data-[state=active]:text-shiftleft-700 rounded-none px-4 py-3 text-sm font-medium">
                        <Shield className="h-4 w-4 mr-2" />
                        System
                    </TabsTrigger>
                </TabsList>

                {/* Appearance Tab - Using shared component */}
                <TabsContent value="appearance" className="mt-6">
                    <AppearanceSettings
                        components={UI_COMPONENTS}
                        title="Appearance"
                        description="Customize the look and feel of your workspace"
                    />
                </TabsContent>

                {/* Other Tabs */}
                <TabsContent value="estate" className="mt-6">
                    <div className="space-y-4">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Estate & Clients</h2>
                            <p className="text-sm text-gray-500">Manage monitored infrastructure</p>
                        </div>
                        <SettingsGrid items={estateItems} />
                    </div>
                </TabsContent>

                <TabsContent value="monitoring" className="mt-6">
                    <div className="space-y-4">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Monitoring & Alerts</h2>
                            <p className="text-sm text-gray-500">Configure detection rules</p>
                        </div>
                        <SettingsGrid items={monitoringItems} />
                    </div>
                </TabsContent>

                <TabsContent value="agent" className="mt-6">
                    <div className="space-y-4">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Agent & AI</h2>
                            <p className="text-sm text-gray-500">Configure AI assistant</p>
                        </div>
                        <SettingsGrid items={agentItems} />
                    </div>
                </TabsContent>

                <TabsContent value="system" className="mt-6">
                    <div className="space-y-4">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">System Health</h2>
                            <p className="text-sm text-gray-500">Platform maintenance</p>
                        </div>
                        <SettingsGrid items={systemItems} />
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}
